import { useEffect, useState } from 'react';
import mainSectionWrapperStyles from '../../../pageStructure/mainSectionWrapper/MainSectionWrapper.module.css';
import styles from './LoanBuilder.module.css';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper';
import { CustomiseOffer } from './customiseOffer/CustomiseOffer';
import { connect } from 'react-redux';
import ApprovalHeader from '../approvalHeader/ApprovalHeader';
import { TitleSectionWrapper } from '../../../pageStructure/titleSectionWrapper/TitleSectionWrapper';
import { Button } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';
import messages from './LoanBuilder.messages';
import { Dispatch } from 'redux';
import { DisplayTable } from './displayTable/DisplayTable';
import { DisplayTableMobile } from './displayTableMobile/DisplayTableMobile';
import ErrorMessage from '../../../ui/errorMessage/ErrorMessage';
import { Error } from '../../MyOffers.types';
import IwMedia from '../../../../iwMedia';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { buildLocationWithSearch } from '../../../drawDown/DrawDownHelper';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { fetchGetFormalOffers } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';
import { getAvailableCash, getTimeOutDate } from '../../MyOffers.helpers';
import EarlyRepaymentCalculator from '../earlyRepaymentCalculator/EarlyRepayment';
import { getMaxDate } from '../earlyRepaymentCalculator/EarlyRepaymentHelper';
import { getMonthsfromDays } from '../../../multiLoanDashboard/accountsSelector';
import { FooterSectionWrapper } from '../../../pageStructure/footerSectionWrapper/FooterSectionwraper';
import { TrustPilot } from '../../../TrustPilot/TrustPilot';
import { getCountry } from '@iwIntlProvider/CountryProvider';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    validateAndStartDrawdown: (
        selectedOfferId: string,
        drawdownAmount: number
    ) => {
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            //@ts-ignore
            location: buildLocationWithSearch({
                pathname: '/funding-requirements/',
                offerId: selectedOfferId,
                amount: drawdownAmount
            })
        });
    }
});

type PropsFromRedux = ReturnType<typeof mapDispatchToProps>;

const LoanBuilder = ({ validateAndStartDrawdown }: PropsFromRedux) => {
    const [drawdownAmount, setDrawdownAmount] = useState<number>(0);
    const [newCalculationRequired, setNewCalculationRequired] = useState(false);
    const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
    const [errors, setErrors] = useState<Error[]>([]);

    const stateKey = useStateKey();

    const hasCTAError = (): Error | undefined | null => {
        return errors && errors.find((err) => err.source === 'Continue CTA');
    };

    const hasEarlyRepaymentCalculatorError = (): Error | undefined | null => {
        return (
            errors &&
            errors.find((err) => err.source === 'Early Repayment Calculator')
        );
    };

    const {
        isLoading: getFormalOffersResponseLoading,
        data: getFormalOffersResponse,
        isError: getFormalOffersResponseError
    } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey: stateKey! })
    });

    useEffect(() => {
        if (getFormalOffersResponse && getFormalOffersResponse.data) {
            setDrawdownAmount(
                getFormalOffersResponse!.data!.formal_offers[0].available_cash!
            );
        }
    }, [getFormalOffersResponse, setDrawdownAmount]);

    useEffect(() => {
        if (selectedOfferId) {
            setErrors([]);
        }
    }, [selectedOfferId, setErrors]);

    if (getFormalOffersResponseLoading) {
        return <MainContentPending data-testid="loan-builder-pending" />;
    }

    if (getFormalOffersResponseError) {
        return null;
    }

    const formalOffers = getFormalOffersResponse.data!.formal_offers;
    const approvalStatusTimeOut = getTimeOutDate(formalOffers);
    const maxCashAmount = getAvailableCash(formalOffers);

    let loanDuration = 0;
    let productType = '';
    let maxDate = new Date().toISOString().slice(0, 10);

    if (selectedOfferId) {
        const selectedOffer = formalOffers.find(
            (offer) => offer.offer_id === selectedOfferId
        );
        loanDuration =
            selectedOffer!.duration!.unit === 'days'
                ? getMonthsfromDays(selectedOffer!.duration!.amount)
                : selectedOffer!.duration!.amount;

        maxDate = getMaxDate(selectedOffer!.duration!.amount);

        productType = selectedOffer!.product_type!;
    }

    const showTable = drawdownAmount > 0 && formalOffers.length > 0;

    return (
        <>
            <TitleSectionWrapper
                wrapperInnerClassName={styles.titleInnerWrapper}
            >
                <ApprovalHeader
                    approvalStatusTimeOut={approvalStatusTimeOut}
                    maxCashAmount={maxCashAmount}
                />
            </TitleSectionWrapper>

            <MainSectionWrapper
                className={styles.mainSection}
                innerWrapperClassName={mainSectionWrapperStyles.innerWrapper}
            >
                <div
                    className={styles.drawdownSection}
                    data-testid="loan-builder-slider"
                >
                    {drawdownAmount > 0 && (
                        <CustomiseOffer
                            drawdownAmount={drawdownAmount}
                            setDrawdownAmount={setDrawdownAmount}
                            newCalculationRequired={newCalculationRequired}
                            setNewCalculationRequired={
                                setNewCalculationRequired
                            }
                            maxCashAmount={maxCashAmount}
                            approvalStatusTimeOut={approvalStatusTimeOut}
                        />
                    )}
                </div>
                <div className={styles.displayTableSection}>
                    {showTable && (
                        <>
                            <IwMedia to="small">
                                <DisplayTableMobile
                                    drawdownAmount={drawdownAmount}
                                    formalOffers={formalOffers}
                                    setSelectedOfferId={setSelectedOfferId}
                                    selectedOfferId={selectedOfferId}
                                />
                            </IwMedia>
                            <IwMedia from="small">
                                <DisplayTable
                                    drawdownAmount={drawdownAmount}
                                    formalOffers={formalOffers}
                                    setSelectedOfferId={setSelectedOfferId}
                                    selectedOfferId={selectedOfferId}
                                />
                            </IwMedia>
                        </>
                    )}
                </div>

                <div
                    className={styles.ctaSection}
                    data-testid="loan-builder-cta"
                >
                    <div className={styles.ctaHeplerText}>
                        <FormattedMessage {...messages.CTAinfo} />
                    </div>
                    <div>
                        <Button
                            variant="success"
                            type="submit"
                            size="large"
                            data-action-name="Approval continue button"
                            onClick={() => {
                                if (
                                    !selectedOfferId ||
                                    selectedOfferId === null
                                ) {
                                    const error: Error = {
                                        source: 'Continue CTA',
                                        type: 'Offer Not Selected'
                                    };
                                    setErrors([...errors, error]);
                                } else {
                                    validateAndStartDrawdown(
                                        selectedOfferId,
                                        drawdownAmount
                                    );
                                }
                            }}
                        >
                            <FormattedMessage
                                {...messages.cta}
                                values={{
                                    amount: (
                                        <MoneyNoDecimals
                                            value={drawdownAmount}
                                        />
                                    )
                                }}
                            />
                        </Button>
                    </div>
                </div>
                {hasCTAError() && (
                    <div
                        className={styles.errorSection}
                        data-testid="loan-builder-cta-error"
                    >
                        <ErrorMessage
                            error={
                                <FormattedMessage {...messages.continueError} />
                            }
                        />
                    </div>
                )}

                <div className={styles.earlyRepaymentCalculator}>
                    <EarlyRepaymentCalculator
                        drawdownAmount={drawdownAmount}
                        offerId={selectedOfferId}
                        loanDuration={loanDuration}
                        productType={productType}
                        maxDate={maxDate}
                        setErrors={setErrors}
                    />
                </div>
                {hasEarlyRepaymentCalculatorError() && (
                    <div
                        className={styles.errorSection}
                        data-testid="loan-builder-early-repayment-error"
                    >
                        <ErrorMessage
                            error={
                                <FormattedMessage
                                    {...messages.earlyRepaymentCalculatorError}
                                />
                            }
                        />
                    </div>
                )}
            </MainSectionWrapper>

            <FooterSectionWrapper>
                {getCountry() === 'UK' && <TrustPilot />}
            </FooterSectionWrapper>
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(LoanBuilder);
